// All Phase-1 data is mocked here. Real Flowise wiring happens later.

export const company = {
  name: "Lumen AI",
  stage: "PRE-SEED",
  tagline: "AI Productivity OS · Incorporated Mar 2026",
  runwayMonths: 9.2,
  burn: "$12k MRR",
  mode: "bootstrap", // or "funding"
};

export const user = { name: "Jordan M.", initials: "JM" };

export const agents = [
  {
    id: "atlas",
    name: "Atlas",
    role: "CTO",
    focus: "Tech architecture · Engineering",
    status: "debating",
    tone: "dark",
    preview:
      "Refactoring to serverless drops infra cost ~40% and removes the bottleneck on async ingestion. The migration path is two sprints with no downtime if we feature-flag.",
  },
  {
    id: "ledger",
    name: "Ledger",
    role: "CFO",
    focus: "Budgets · Runway · Projections",
    status: "thinking",
    tone: "blue",
    preview:
      "Modeling Q3 burn: current runway is 9.2 months at $12k MRR growth. A 6-week contractor slot fits within reserves without touching the bridge buffer.",
  },
  {
    id: "aura",
    name: "Aura",
    role: "CMO",
    focus: "Growth · Brand · GTM",
    status: "idle",
    tone: "neutral",
    preview:
      "Awaiting product launch confirmation to trigger the waitlist re-activation drip. 1,400 warm signups primed; we can sequence within 24 hours.",
  },
  {
    id: "nexus",
    name: "Nexus",
    role: "COO",
    focus: "Operations · Scaling · Compliance",
    status: "debating",
    tone: "lavender",
    preview:
      "We need SOC2 Type I before targeting enterprise. 45-day window if we start the vendor onboarding this week. Otherwise we lose Q3 deals.",
  },
  {
    id: "prism",
    name: "Prism",
    role: "Product",
    focus: "UX · Roadmap · Discovery",
    status: "thinking",
    tone: "mint",
    preview:
      "Onboarding drop-off at step 3 needs a guided tour. Heatmaps show 60% of churn flows from a single confusing input — fixable in one sprint.",
  },
  {
    id: "hunter",
    name: "Hunter",
    role: "Sales",
    focus: "Pipeline · Outbound · Closing",
    status: "idle",
    tone: "peach",
    preview:
      "Pipeline at $186k ARR — 12 deals in late stage. If onboarding lands clean, I can close 4 of them inside 30 days and unblock the design partner cohort.",
  },
  {
    id: "echo",
    name: "Echo",
    role: "EA",
    focus: "Coordination · Comms · Scheduling",
    status: "idle",
    tone: "sky",
    preview:
      "Calendar synced. 3 investor intros queued for next week, sprint kickoff locked for Monday, SOC2 vendor demo pending Thursday confirmation.",
  },
  {
    id: "vault",
    name: "Vault",
    role: "Admin",
    focus: "Legal · HR · Compliance",
    status: "thinking",
    tone: "stone",
    preview:
      "Drafting contractor MSA template for engineering hires plus a stripped IP-assignment rider. Both ready for review within 48 hours.",
  },
];

// Scripted multi-agent debate played back when user submits a goal.
export const debateScript = [
  {
    agentId: "atlas",
    text: "If we ship onboarding v1 in 2 sprints we cut churn ~28%. Engineering capacity is the constraint — I want a contractor on async pipelines.",
  },
  {
    agentId: "ledger",
    text: "Burn projection holds. We can absorb a contractor for 6 weeks without touching reserves. Bridge round is unnecessary at this MRR.",
  },
  {
    agentId: "aura",
    text: "Waitlist is warm — 1,400 signups. I can run a re-activation drip the day onboarding lands and stage a soft PR push 72 hours later.",
  },
  {
    agentId: "nexus",
    text: "If we go enterprise, SOC2 Type I needs to start now. 45 days minimum and procurement gates close otherwise.",
  },
  {
    agentId: "prism",
    text: "Drop-off at step 3 is the bleed. A guided tour fixes 60% of that based on the heatmaps; I'll spec it tomorrow.",
  },
  {
    agentId: "hunter",
    text: "12 deals in late stage. If onboarding lands clean I can close 4 in 30 days — that's $58k net new ARR.",
  },
  {
    agentId: "vault",
    text: "Drafting MSA + contractor agreements now so hiring isn't gated on legal. IP assignment included by default.",
  },
  {
    agentId: "echo",
    text: "I'll lock the sprint kickoff and book the SOC2 vendor demo for Thursday. Investor intros pushed to next week.",
  },
];

// Lookup map for fast agent fetch by id.
export const agentMap = Object.fromEntries(agents.map((a) => [a.id, a]));

// Tone -> Tailwind/inline gradient surface for AgentCard background.
export const toneSurface = {
  dark: {
    bg: "bg-[#0B0B0E]",
    text: "text-white",
    border: "border-black/20",
    subtle: "text-white/60",
    quote: "text-white/85",
    eyebrow: "text-white/50",
    link: "text-white/80 hover:text-white",
    chip: "bg-white/10 text-white/90",
  },
  blue: {
    bg: "bg-gradient-to-b from-[#EAF1FF] to-white",
    text: "text-zinc-900",
    border: "border-black/[0.04]",
    subtle: "text-zinc-500",
    quote: "text-zinc-700",
    eyebrow: "text-zinc-500",
    link: "text-zinc-700 hover:text-black",
    chip: "bg-white/70 text-zinc-700",
  },
  neutral: {
    bg: "bg-gradient-to-b from-[#FAFAFA] to-white",
    text: "text-zinc-900",
    border: "border-black/[0.04]",
    subtle: "text-zinc-500",
    quote: "text-zinc-700",
    eyebrow: "text-zinc-500",
    link: "text-zinc-700 hover:text-black",
    chip: "bg-zinc-100 text-zinc-700",
  },
  lavender: {
    bg: "bg-gradient-to-b from-[#F2EBFF] to-white",
    text: "text-zinc-900",
    border: "border-black/[0.04]",
    subtle: "text-zinc-500",
    quote: "text-zinc-700",
    eyebrow: "text-zinc-500",
    link: "text-zinc-700 hover:text-black",
    chip: "bg-white/70 text-zinc-700",
  },
  mint: {
    bg: "bg-gradient-to-b from-[#EAF7F0] to-white",
    text: "text-zinc-900",
    border: "border-black/[0.04]",
    subtle: "text-zinc-500",
    quote: "text-zinc-700",
    eyebrow: "text-zinc-500",
    link: "text-zinc-700 hover:text-black",
    chip: "bg-white/70 text-zinc-700",
  },
  peach: {
    bg: "bg-gradient-to-b from-[#FFF1E6] to-white",
    text: "text-zinc-900",
    border: "border-black/[0.04]",
    subtle: "text-zinc-500",
    quote: "text-zinc-700",
    eyebrow: "text-zinc-500",
    link: "text-zinc-700 hover:text-black",
    chip: "bg-white/70 text-zinc-700",
  },
  sky: {
    bg: "bg-gradient-to-b from-[#E8F4FA] to-white",
    text: "text-zinc-900",
    border: "border-black/[0.04]",
    subtle: "text-zinc-500",
    quote: "text-zinc-700",
    eyebrow: "text-zinc-500",
    link: "text-zinc-700 hover:text-black",
    chip: "bg-white/70 text-zinc-700",
  },
  stone: {
    bg: "bg-gradient-to-b from-[#F1EEE8] to-white",
    text: "text-zinc-900",
    border: "border-black/[0.04]",
    subtle: "text-zinc-500",
    quote: "text-zinc-700",
    eyebrow: "text-zinc-500",
    link: "text-zinc-700 hover:text-black",
    chip: "bg-white/70 text-zinc-700",
  },
};

export const statusMeta = {
  debating: { label: "DEBATING", dot: "#7C3AED" },
  thinking: { label: "THINKING", dot: "#2563EB" },
  idle: { label: "IDLE", dot: "#9CA3AF" },
};

// Phase 5 — Settings: per-agent impact text shown when an agent is toggled off.
export const agentImpactMessages = {
  atlas: "Without Atlas, the boardroom can't score engineering trade-offs.",
  ledger: "Without Ledger, runway and burn projections go silent.",
  aura: "Without Aura, GTM and growth recommendations stop appearing.",
  nexus: "Without Nexus, compliance and ops risk lose their watchdog.",
  prism: "Without Prism, product and UX critique disappears from debates.",
  hunter: "Without Hunter, pipeline and revenue motion drop off the table.",
  echo: "Without Echo, calendar and intro coordination won't surface.",
  vault: "Without Vault, legal and HR risks slip past unflagged.",
};

// Phase 5 — Operating mode emphasis (which agents lead the debate per mode).
export const modeEmphasis = {
  bootstrap: {
    agents: ["ledger", "prism", "hunter"],
    label: "Capital efficiency · profitable from day one",
  },
  funding: {
    agents: ["ledger", "aura", "nexus", "atlas"],
    label: "Growth velocity · hire fast · scale operations",
  },
};

// ---------------------------------------------------------------------------
// Phase 2 — Agents 1:1 chat threads
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Phase 2 — Tasks dashboard data
// ---------------------------------------------------------------------------

export const TASK_STATUSES = ["todo", "in_progress", "blocked", "done"];

export const taskStatusMeta = {
  todo: { label: "To do", dot: "#9CA3AF", chip: "bg-zinc-100 text-zinc-700" },
  in_progress: { label: "In progress", dot: "#2563EB", chip: "bg-blue-50 text-blue-700" },
  blocked: { label: "Blocked", dot: "#EF4444", chip: "bg-rose-50 text-rose-700" },
  done: { label: "Done", dot: "#10B981", chip: "bg-emerald-50 text-emerald-700" },
};

export const taskPriorityMeta = {
  low: { label: "Low", chip: "bg-zinc-100 text-zinc-600" },
  med: { label: "Med", chip: "bg-amber-50 text-amber-700" },
  high: { label: "High", chip: "bg-orange-100 text-orange-700" },
  urgent: { label: "Urgent", chip: "bg-rose-100 text-rose-700" },
};

// ---------------------------------------------------------------------------
// Phase 3 — 90-Day Roadmap
// ---------------------------------------------------------------------------

export const roadmap = {
  totalDays: 90,
  currentDay: 24,
  onTrackPercent: 62,
  phases: [
    {
      id: "p1",
      name: "Foundation",
      weeks: "Week 1–2",
      startDay: 1,
      endDay: 14,
      color: "mint",
      status: "done",
      tagline: "Incorporate · validate · sketch v0",
    },
    {
      id: "p2",
      name: "Build",
      weeks: "Week 3–6",
      startDay: 15,
      endDay: 42,
      color: "blue",
      status: "in_progress",
      tagline: "Ship MVP · onboard 10 design partners",
    },
    {
      id: "p3",
      name: "Launch",
      weeks: "Week 7–12",
      startDay: 43,
      endDay: 90,
      color: "lavender",
      status: "upcoming",
      tagline: "Public launch · first 100 paying customers",
    },
  ],
  milestones: [
    { id: "m1", day: 14, label: "Incorporation complete", status: "done" },
    { id: "m2", day: 28, label: "MVP private beta live", status: "at_risk" },
    { id: "m3", day: 56, label: "10 paying design partners", status: "upcoming" },
    { id: "m4", day: 84, label: "Public launch + waitlist drop", status: "upcoming" },
  ],
  tasks: [
    // Phase 1 — Foundation
    { id: "rt1",  phaseId: "p1", title: "File Delaware C-Corp",            owner: "HUMAN", priority: "urgent", startDay: 1,  durationDays: 5,  progress: 100, status: "done" },
    { id: "rt2",  phaseId: "p1", title: "Founder IP assignments",          owner: "HUMAN", priority: "high",   startDay: 3,  durationDays: 4,  progress: 100, status: "done" },
    { id: "rt3",  phaseId: "p1", title: "Open Mercury account",            owner: "HUMAN", priority: "high",   startDay: 4,  durationDays: 3,  progress: 100, status: "done" },
    { id: "rt4",  phaseId: "p1", title: "Validate problem (15 calls)",     owner: "aura",  priority: "high",   startDay: 5,  durationDays: 8,  progress: 100, status: "done" },
    { id: "rt5",  phaseId: "p1", title: "v0 wireframes + design system",   owner: "prism", priority: "med",    startDay: 7,  durationDays: 6,  progress: 100, status: "done" },
    // Phase 2 — Build
    { id: "rt6",  phaseId: "p2", title: "Auth + workspace scaffolding",    owner: "atlas", priority: "high",   startDay: 15, durationDays: 5,  progress: 100, status: "done" },
    { id: "rt7",  phaseId: "p2", title: "Boardroom multi-agent engine",    owner: "atlas", priority: "urgent", startDay: 18, durationDays: 12, progress: 70,  status: "in_progress" },
    { id: "rt8",  phaseId: "p2", title: "Onboarding v1 (guided tour)",     owner: "prism", priority: "urgent", startDay: 20, durationDays: 9,  progress: 55,  status: "in_progress" },
    { id: "rt9",  phaseId: "p2", title: "Pricing + Stripe billing",        owner: "ledger",priority: "high",   startDay: 24, durationDays: 6,  progress: 30,  status: "in_progress" },
    { id: "rt10", phaseId: "p2", title: "10 design partner outreach",      owner: "hunter",priority: "high",   startDay: 26, durationDays: 14, progress: 25,  status: "in_progress" },
    { id: "rt11", phaseId: "p2", title: "SOC2 Type I kickoff",             owner: "nexus", priority: "med",    startDay: 30, durationDays: 12, progress: 10,  status: "in_progress" },
    { id: "rt12", phaseId: "p2", title: "Investor deck v1",                owner: "ledger",priority: "med",    startDay: 35, durationDays: 7,  progress: 0,   status: "todo" },
    // Phase 3 — Launch
    { id: "rt13", phaseId: "p3", title: "Public site + waitlist drop",     owner: "aura",  priority: "high",   startDay: 49, durationDays: 7,  progress: 0,   status: "todo" },
    { id: "rt14", phaseId: "p3", title: "Launch playbook + PR",            owner: "aura",  priority: "high",   startDay: 56, durationDays: 7,  progress: 0,   status: "todo" },
    { id: "rt15", phaseId: "p3", title: "Sales pipeline → 50 demos",       owner: "hunter",priority: "urgent", startDay: 60, durationDays: 14, progress: 0,   status: "todo" },
    { id: "rt16", phaseId: "p3", title: "Post-launch retention loop",      owner: "prism", priority: "med",    startDay: 70, durationDays: 14, progress: 0,   status: "todo" },
    { id: "rt17", phaseId: "p3", title: "Bridge round prep",               owner: "ledger",priority: "med",    startDay: 80, durationDays: 10, progress: 0,   status: "todo" },
  ],
};

export const phaseColorMap = {
  mint:     { band: "#EAF7F0", solid: "#10B981", soft: "rgba(16,185,129,0.10)" },
  blue:     { band: "#EAF1FF", solid: "#2563EB", soft: "rgba(37,99,235,0.10)" },
  lavender: { band: "#F2EBFF", solid: "#7C3AED", soft: "rgba(124,58,237,0.10)" },
};

export const priorityStripColor = {
  urgent: "#EF4444",
  high: "#F97316",
  med: "#2563EB",
  low: "#9CA3AF",
};

// ---------------------------------------------------------------------------
// Phase 3 — Venture Vault
// ---------------------------------------------------------------------------

export const vault = {
  readinessPercent: 64,
  totalDocs: 22,
  completeDocs: 14,
  sections: [
    {
      id: "deck",
      name: "Pitch Deck",
      icon: "Presentation",
      lastUpdated: "2d ago",
      updatedBy: "aura",
      status: "incomplete",
      docs: [
        { id: "d1", name: "Pitch deck v3.pdf",          kind: "pdf",   size: "4.2 MB", status: "complete", updatedAt: "2d ago" },
        { id: "d2", name: "One-pager.pdf",              kind: "pdf",   size: "880 KB", status: "complete", updatedAt: "5d ago" },
        { id: "d3", name: "Demo video script.docx",     kind: "doc",   size: "120 KB", status: "draft",    updatedAt: "1d ago" },
        { id: "d4", name: "Product walkthrough video", kind: "video", size: "—",       status: "missing",  updatedAt: "—" },
      ],
    },
    {
      id: "financials",
      name: "Financials",
      icon: "LineChart",
      lastUpdated: "1d ago",
      updatedBy: "ledger",
      status: "incomplete",
      docs: [
        { id: "f1", name: "3-year financial model.xlsx",  kind: "sheet", size: "2.1 MB", status: "complete", updatedAt: "1d ago" },
        { id: "f2", name: "Cap table v2.xlsx",            kind: "sheet", size: "320 KB", status: "complete", updatedAt: "4d ago" },
        { id: "f3", name: "Burn + runway dashboard.pdf",  kind: "pdf",   size: "640 KB", status: "complete", updatedAt: "1d ago" },
        { id: "f4", name: "Unit economics memo.docx",     kind: "doc",   size: "210 KB", status: "complete", updatedAt: "3d ago" },
        { id: "f5", name: "Pricing model worksheet.xlsx", kind: "sheet", size: "140 KB", status: "review",   updatedAt: "12h ago" },
        { id: "f6", name: "Investor FAQ memo.docx",       kind: "doc",   size: "—",      status: "missing",  updatedAt: "—" },
      ],
    },
    {
      id: "legal",
      name: "Legal Documents",
      icon: "Scale",
      lastUpdated: "5d ago",
      updatedBy: "vault",
      status: "missing",
      docs: [
        { id: "l1", name: "Certificate of Incorporation.pdf", kind: "pdf", size: "1.1 MB", status: "complete", updatedAt: "21d ago" },
        { id: "l2", name: "Founder IP assignments.pdf",        kind: "pdf", size: "780 KB", status: "complete", updatedAt: "18d ago" },
        { id: "l3", name: "Bylaws.pdf",                        kind: "pdf", size: "640 KB", status: "complete", updatedAt: "14d ago" },
        { id: "l4", name: "Stock purchase agreements.pdf",     kind: "pdf", size: "1.4 MB", status: "complete", updatedAt: "12d ago" },
        { id: "l5", name: "Contractor MSA template.docx",      kind: "doc", size: "—",      status: "missing",  updatedAt: "—" },
        { id: "l6", name: "Privacy policy & ToS.docx",         kind: "doc", size: "—",      status: "missing",  updatedAt: "—" },
        { id: "l7", name: "DPA template.docx",                 kind: "doc", size: "—",      status: "missing",  updatedAt: "—" },
      ],
    },
    {
      id: "product",
      name: "Product Assets",
      icon: "Box",
      lastUpdated: "3d ago",
      updatedBy: "prism",
      status: "incomplete",
      docs: [
        { id: "p1", name: "Brand guidelines.pdf",     kind: "pdf",   size: "12 MB", status: "complete", updatedAt: "8d ago" },
        { id: "p2", name: "Logo lockups.zip",         kind: "image", size: "44 MB", status: "complete", updatedAt: "8d ago" },
        { id: "p3", name: "Product screenshots.zip", kind: "image", size: "—",      status: "missing",  updatedAt: "—" },
        { id: "p4", name: "Demo video — final.mp4",  kind: "video", size: "—",      status: "missing",  updatedAt: "—" },
        { id: "p5", name: "Press kit.pdf",            kind: "pdf",   size: "—",     status: "missing",  updatedAt: "—" },
      ],
    },
  ],
};

export const docStatusMeta = {
  complete: { label: "COMPLETE", dot: "#10B981", chip: "bg-emerald-50 text-emerald-700" },
  draft:    { label: "DRAFT",    dot: "#2563EB", chip: "bg-blue-50 text-blue-700" },
  review:   { label: "REVIEW",   dot: "#7C3AED", chip: "bg-violet-50 text-violet-700" },
  missing:  { label: "MISSING",  dot: "#F97316", chip: "bg-orange-100 text-orange-700" },
};

export const sectionStatusMeta = {
  ready:      { label: "READY",      chip: "bg-emerald-50 text-emerald-700" },
  incomplete: { label: "INCOMPLETE", chip: "bg-zinc-100 text-zinc-700" },
  missing:    { label: "MISSING",    chip: "bg-orange-100 text-orange-700" },
};

// ---------------------------------------------------------------------------
// Phase 4 — Landing Page
// ---------------------------------------------------------------------------

export const landing = {
  socialProof: ["Lumen", "Northstar", "Vellum", "Fjord", "Mercato", "Halo", "Beacon", "Quartz"],
  agentTaglines: {
    atlas:  "Architects your stack and ships engineering decisions.",
    ledger: "Models burn, runway, and your fundraise math.",
    aura:   "Owns brand, GTM, and demand generation.",
    nexus:  "Runs operations, scaling, and compliance.",
    prism:  "Drives product strategy, UX, and retention loops.",
    hunter: "Builds pipeline and closes your first 50 customers.",
    echo:   "Coordinates your calendar, comms, and intros.",
    vault:  "Handles legal, HR, and risk.",
  },
  faqs: [
    {
      q: "Is Idea AI replacing my team?",
      a: "No. Idea AI gives a solo founder the leverage of a fully staffed exec team while you're early. Once you hire real humans, agents become their force multipliers — not their replacements.",
    },
    {
      q: "How does the multi-agent debate actually work?",
      a: "When you set a goal, every relevant agent reasons in parallel, surfaces evidence, then converges on a recommendation with confidence scoring, strategy, and risk. You see the full debate, not just the answer.",
    },
    {
      q: "Can I bring my own data and documents?",
      a: "Yes. Drop pitch decks, financial models, legal docs, and product assets into Vault. Agents reference them when they reason — every recommendation is grounded in your actual context.",
    },
    {
      q: "Where does the AI run? Is my data private?",
      a: "Your data is encrypted at rest and in transit. We never train models on your private data. Per-workspace isolation by default; SOC2 Type II in the Funding plan.",
    },
    {
      q: "Can I disable agents I don't want?",
      a: "Yes. Toggle any of the eight agents off in Settings. The boardroom shows the impact of each toggle — e.g., higher financial risk if your CFO is silenced.",
    },
    {
      q: "What integrations do you support?",
      a: "Slack, Linear, Notion, Stripe, Mercury, Brex, DocuSign, and Zapier on the Funding plan. More — including HubSpot, GitHub, and Plaid — on the roadmap.",
    },
  ],
};

